import { useState, forwardRef, useImperativeHandle } from "react";
import "./TableColor.css";

export const TableColor = forwardRef(({ initialBackgroundColor, initialAccentColor }, ref) => {
    const [colorTop, setColorTop] = useState(initialBackgroundColor || "#ffffff");
    const [generatedColorsTop, setGeneratedColorsTop] = useState([]);
    const [colorBottom, setColorBottom] = useState(initialAccentColor || "#ffffff");
    const [generatedColorsBottom, setGeneratedColorsBottom] = useState([]);

    const getBaseColors = () => {
        return {
            backgroundColor: colorTop,
            accentColor: colorBottom
        };
    };

    useImperativeHandle(ref, () => ({
        getBaseColors // Solo exponemos esta función ahora
    }));


    const hexToRgb = (hex) => {
        hex = hex.replace(/^#/, "");
        if (hex.length === 3) {
            hex = hex.split("").map((x) => x + x).join("");
        }
        const bigint = parseInt(hex, 16);
        return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    };

    const rgbToHsl = (r, g, b) => {
        r /= 255;
        g /= 255;
        b /= 255;
        const max = Math.max(r, g, b),
            min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;
        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
                default:
                    h=0;
                    break;
            }
            h /= 6;
        }
        return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
    };

    const hslToHex = (h, s, l) => {
        s /= 100;
        l /= 100;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;

        if (0 <= h && h < 60) {
            r = c; g = x; b = 0;
        } else if (60 <= h && h < 120) {
            r = x; g = c; b = 0;
        } else if (120 <= h && h < 180) {
            r = 0; g = c; b = x;
        } else if (180 <= h && h < 240) {
            r = 0; g = x; b = c;
        } else if (240 <= h && h < 300) {
            r = x; g = 0; b = c;
        } else if (300 <= h && h < 360) {
            r = c; g = 0; b = x;
        }

        r = Math.round((r + m) * 255);
        g = Math.round((g + m) * 255);
        b = Math.round((b + m) * 255);

        return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    };

    const generateColors = (inputColor, setGeneratedColors, isAccent = false) => {
        let color = inputColor.trim();
        if (!color.startsWith("#")) {
            color = "#" + color;
        }

        if (!/^#([0-9A-F]{3}){1,2}$/i.test(color)) {
            alert("Please enter a valid HEX color code.");
            return;
        }

        const baseRgb = hexToRgb(color);
        const [baseH, baseS] = rgbToHsl(...baseRgb);
        const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

        const colors = steps.map((l) => {
            const lightness = l / 10;
            const hslVariable = `${baseH}, ${baseS}%, ${lightness}%`;
            const hexValue = hslToHex(baseH, baseS, lightness);
            return {
                variable: isAccent ? `--ca${l}` : `--cb${l}`,
                hsl: hslVariable,
                hex: hexValue,
                lightness: l,
            };
        });

        setGeneratedColors(colors);
    };

    function getTextColor(hex) {
        const rgb = hexToRgb(hex);
        const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
        return brightness > 128 ? "black" : "white";
    }

    return (
        <div className="table-color-container">
            <div className="input-section">
                <div className="input-container">
                    <h2 className="heading">Background Color</h2>
                    <div className="input-group">
                        <div className="color-box-picker" onClick={() => document.getElementById("colorTopInput").click()}>
                            <div className="color-display" style={{ backgroundColor: colorTop }}></div>
                            <input
                                type="color"
                                id="colorTopInput"
                                value={colorTop}
                                onChange={(e) => setColorTop(e.target.value)}
                                className="hidden-color-input" />
                        </div>
                        <input
                            type="text"
                            value={colorTop}
                            onChange={(e) => {
                                let val = e.target.value;
                                if (val && !val.startsWith('#')) {
                                    val = '#' + val.replace(/^#*/, '');
                                }
                                setColorTop(val);
                            }}
                            className="color-input" />
                        <button className="btn-submit" onClick={() => generateColors(colorTop, setGeneratedColorsTop, false)}>
                            Generar
                        </button>
                    </div>
                </div>
                <div className="colors-container">
                    <div className="colors-row">
                        {generatedColorsTop.map((c, index) => (
                            <div key={index} className="color-box">
                                <div className="color-info"
                                    style={{ backgroundColor: c.hex, color: getTextColor(c.hex), }} >
                                    <div className="color-hex">{c.hex}</div>
                                    <div className="color-variable">{c.variable}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="input-section">
                <div className="input-container">
                    <h2 className="heading">Accent Color</h2>
                    <div className="input-group">
                        <div className="color-box-picker" onClick={() => document.getElementById("colorBottomInput").click()}>
                            <div className="color-display" style={{ backgroundColor: colorBottom }}></div>
                            <input
                                type="color"
                                id="colorBottomInput"
                                value={colorBottom}
                                onChange={(e) => setColorBottom(e.target.value)}
                                className="hidden-color-input"
                            />
                        </div>
                        <input
                            type="text"
                            value={colorBottom}
                            onChange={(e) => {
                                let val = e.target.value;
                                if (val && !val.startsWith('#')) {
                                    val = '#' + val.replace(/^#*/, '');
                                }
                                setColorBottom(val);
                            }}
                            className="color-input"
                        />
                        <button className="btn-submit" onClick={() => generateColors(colorBottom, setGeneratedColorsBottom, true)}>
                            Generar
                        </button>
                    </div>
                </div>
                <div className="colors-container">
                    <div className="colors-row">
                        {generatedColorsBottom.map((c, index) => (
                            <div key={index} className="color-box">
                                <div className="color-info" style={{ backgroundColor: c.hex, color: getTextColor(c.hex), }}>
                                    <div className="color-hex">{c.hex}</div>
                                    <div className="color-variable">{c.variable}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default TableColor;